function fail() { throw Object.assign(new Error("TREE_MOVE_INVALID"), { status: 422 }); }

// A move is scoped to one encrypted plant document; IDs and Twin ownership never change.
export function moveTreeNode(document, nodeId, targetId, placement) {
  const nodes = new Map(), parents = new Map();
  function index(node, parent = null) {
    if (!node?.id || nodes.has(node.id)) fail();
    nodes.set(node.id, node); parents.set(node.id, parent);
    (node.children || []).forEach(child => index(child, node.id));
  }
  index(document.tree);
  const node = nodes.get(nodeId), target = nodes.get(targetId);
  if (!node || !target || nodeId === document.tree.id || nodeId === targetId ||
      node.type === "document" || target.type === "document" ||
      !["inside", "before", "after"].includes(placement)) fail();
  const parentId = placement === "inside" ? targetId : parents.get(targetId);
  if (!parentId || (placement !== "inside" && parents.get(nodeId) !== parentId)) fail();
  for (let id = parentId; id; id = parents.get(id)) if (id === nodeId) fail();
  const reparented = parents.get(nodeId) !== parentId;
  const moved = structuredClone(node);
  if (Object.hasOwn(moved, "parentId")) moved.parentId = parentId;
  const tree = structuredClone(document.tree);
  function update(current) {
    if (current.children) current.children = current.children.filter(child => child.id !== nodeId);
    (current.children || []).forEach(update);
    if (current.id === parentId) {
      current.children ||= [];
      const offset = placement === "inside" ? current.children.length :
        current.children.findIndex(child => child.id === targetId) + (placement === "after" ? 1 : 0);
      current.children.splice(offset, 0, moved);
    }
  }
  update(tree);
  let media = document.media;
  if (reparented) {
    const ownerOf = id => {
      while (id && !document.media?.[id]) id = parents.get(id);
      return id;
    };
    const oldOwner = ownerOf(parents.get(nodeId)), newOwner = ownerOf(parentId);
    const oldImage = document.media?.[oldOwner], newImage = document.media?.[newOwner];
    const sameImage = Boolean(oldImage?.imageSrc && oldImage.imageSrc === newImage?.imageSrc &&
      (!oldImage.imageId || !newImage.imageId || oldImage.imageId === newImage.imageId));
    const retainedBoxes = sameImage ? (oldImage.areas || []).filter(area => area.targetId === nodeId)
      .map(area => ({ ...area, parentId })) : [];
    const subtree = new Set();
    const visit = current => { subtree.add(current.id); (current.children || []).forEach(visit); };
    visit(node);
    media = structuredClone(document.media || {});
    // Freeze inherited imagery for internal views before severing the old ancestry.
    // The moved node's external box is discarded; descendant boxes keep their image.
    for (const id of subtree) {
      if (nodes.get(id).type === "document" || media[id]) continue;
      let owner = parents.get(id);
      while (owner && !media[owner]) owner = parents.get(owner);
      if (owner && !subtree.has(owner)) {
        media[id] = { ...structuredClone(media[owner]),
          areas: (media[owner].areas || []).filter(area =>
            subtree.has(area.targetId) && area.targetId !== nodeId) };
      }
    }
    for (const [owner, value] of Object.entries(media)) {
      if (value.areas) value.areas = value.areas.filter(area =>
        area.targetId !== nodeId && (subtree.has(owner) || !subtree.has(area.targetId)));
    }
    if (retainedBoxes.length) media[newOwner].areas = [...(media[newOwner].areas || []), ...retainedBoxes];
  }
  return { document: { ...document, tree, ...(media ? { media } : {}) }, parentId, reparented };
}
